import {
  BadgeTemplateMetadata,
  TemplateAssetsResponse,
  UploadTemplateAssetsPayload,
} from "@/types";
import client from "../client";
import { UseQueryResult, UseSuspenseQueryResult } from "@tanstack/react-query";
import {
  useBuildingQuery,
  useBuildingSuspenseQuery,
} from "@/components/RouterSetup";

export const uploadBadgeTemplate = async (
  payload: UploadTemplateAssetsPayload,
) => {
  try {
    const response = await client.post(`/uploadBadgeTemplate`, payload);
    return response.data.templateId;
  } catch (error) {
    console.error("Error uploading badge template:", error);
  }
};

export const useTemplates = (): UseQueryResult<BadgeTemplateMetadata[]> => {
  return useBuildingQuery({
    queryKey: ["badge-templates"],
    queryFn: async () => {
      try {
        const response = await client.get("/getTemplatesMetadata");
        return response.data as BadgeTemplateMetadata[];
      } catch (error) {
        console.error("Error fetching badge templates:", error);
        return [];
      }
    },
  });
};

export const templateAssetsOption = (
  templateId: string,
  companyId?: string,
) => {
  return {
    queryKey: ["template-assets", templateId, companyId],
    queryFn: async () => {
      try {
        const response = await client.get("/getTemplateAssetsById", {
          params: { templateId, companyId },
        });
        return response.data as TemplateAssetsResponse;
      } catch (error) {
        console.error("Error fetching template assets:", error);
        throw error;
      }
    },
  };
};

export const useTemplateAssets = (
  templateId: string,
  companyId?: string,
): UseSuspenseQueryResult<TemplateAssetsResponse> => {
  return useBuildingSuspenseQuery(templateAssetsOption(templateId, companyId));
};

export const useTemplatesForCompany = (
  companyId?: string,
  isMultiCompany?: boolean,
): UseQueryResult<BadgeTemplateMetadata[]> => {
  return useBuildingQuery({
    queryKey: ["templates-for-company", companyId, isMultiCompany],
    queryFn: async () => {
      const url = isMultiCompany
        ? `/getGeneralTemplates`
        : `/getTemplatesForCompany?companyId=${companyId}`;
      try {
        const response = await client.get(url);
        return response.data as BadgeTemplateMetadata[];
      } catch (error) {
        console.error("Error fetching templates for company:", error);
        throw error;
      }
    },
    enabled: !!companyId || isMultiCompany,
  });
};

export const deleteTemplate = async (templateId: string) => {
  return await client.delete("/deleteTemplate", {
    data: { templateId },
  });
};

export const updateBadgeTemplate = async (
  templateId: string,
  updateData: Partial<BadgeTemplateMetadata>,
) => {
  return await client.patch("/updateTemplate", {
    templateId,
    updateData,
  });
};
